// HoldInvoiceNeedsDecision.tsx: the aggressive popup when a hold payment
// arrives (zz-support-hold-invoices-fable.txt 10.6). Not dismissable by a
// click outside or Escape, but "Decide later" always works; the badge, the
// banner and the 10-minute re-nag keep going.
import { observer } from "mobx-react";
import React, { useEffect } from "react";
import { Modal, Stack, Text } from "@mantine/core";
import { IconHourglass } from "@tabler/icons-react";
import { BOB_MODALS } from "../../helpers/constants";
import { Bob } from "../../stores/Bob";
import { HoldDecisionCard } from "../details/holdInvoiceCard";
import { CustomModalTitle } from "../details/customModalTitle";

const HoldInvoiceNeedsDecision = observer(({ bob }: { bob: Bob }) => {
  const isOpen = bob.openModal === BOB_MODALS.HOLD_INVOICE_NEEDS_DECISION;
  const undecided = bob.undecidedHoldInvoices;
  // the one we opened for, while it is still accepted (it may be settling or
  // rejecting), else the oldest undecided
  const chosen = bob.acceptedHoldInvoices.find(
    (hold) => hold.uuid === bob.decisionModalHoldUuid,
  );
  const hold = chosen ?? undecided[0];
  const others = undecided.filter((h) => h.uuid !== hold?.uuid).length;

  // nothing left to decide here (settled, rejected, or decided in another tab)
  useEffect(() => {
    if (isOpen && !hold && bob.holdInvoiceBusyUuid === null) {
      bob.setOpenModal(null);
    }
  }, [isOpen, hold, bob.holdInvoiceBusyUuid]);

  const decideLater = () => {
    bob.workspace.markHoldModalInteraction();
    bob.setOpenModal(null);
  };

  if (!isOpen || !hold) return null;

  return (
    <Modal
      opened={isOpen}
      onClose={decideLater}
      closeOnClickOutside={false}
      closeOnEscape={false}
      withCloseButton={false}
      title={
        <CustomModalTitle
          text="Payment waiting for your decision"
          icon={<IconHourglass />}
        />
      }
    >
      <Stack>
        {/* keyed: a half-done "Yes, reject" must never carry over to
            another payment when the modal moves on */}
        <HoldDecisionCard
          key={hold.uuid}
          bob={bob}
          hold={hold}
          onDecideLater={decideLater}
        />
        {others > 0 && (
          <Text fz="sm" c="dimmed" ta="center">
            and {others} more waiting under Receive → Hold Invoices
          </Text>
        )}
      </Stack>
    </Modal>
  );
});

export default HoldInvoiceNeedsDecision;
